
import { CONFERENCE_WILL_JOIN, CONFERENCE_WILL_LEAVE } from '../conference';
import { SET_CONFIG } from '../config';
import { MHConferenceEvents } from '../lib-meet-hour';
import { MiddlewareRegistry } from '../redux';
import { getMeetHourGlobalNS } from '../util';

import { setConnectionState } from './actions';
import {
    getRemoteVideoType,
    isLargeVideoReceived,
    isRemoteVideoReceived,
    isTestModeEnabled
} from './functions';
import logger from './logger';

/**
 * Stored bound handlers so they can be removed on conference leave.
 */
let _connectionHandlers = null;

/**
 * The Redux middleware of the feature testing.
 *
 * @param {Store} store - The Redux store.
 * @returns {Function}
 * @private
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case CONFERENCE_WILL_JOIN:
        _bindConferenceConnectionListener(action.conference, store);
        break;
    case CONFERENCE_WILL_LEAVE:
        _unbindConferenceConnectionListener(action.conference);
        break;
    case SET_CONFIG: {
        const result = next(action);

        _bindTortureHelpers(store);

        return result;
    }
    }

    return next(action);
});

/**
 * Binds a handler which will listen for the connection related conference
 * events (in the lib-meet-hour internals those are associated with the ICE
 * connection state).
 *
 * @param {MHConference} conference - The {@link MHConference} for which
 * the conference will join even is dispatched.
 * @param {Store} store - The redux store in which the specified action is being
 * dispatched.
 * @private
 * @returns {void}
 */
function _bindConferenceConnectionListener(conference, { dispatch }) {
    // Clean up any previous handlers first.
    _unbindConferenceConnectionListener(conference);

    const onEstablished = _onConnectionEvent.bind(null, MHConferenceEvents.CONNECTION_ESTABLISHED, dispatch);
    const onRestored = _onConnectionEvent.bind(null, MHConferenceEvents.CONNECTION_RESTORED, dispatch);
    const onInterrupted = _onConnectionEvent.bind(null, MHConferenceEvents.CONNECTION_INTERRUPTED, dispatch);

    _connectionHandlers = { conference,
        onEstablished,
        onRestored,
        onInterrupted };

    conference.on(MHConferenceEvents.CONNECTION_ESTABLISHED, onEstablished);
    conference.on(MHConferenceEvents.CONNECTION_RESTORED, onRestored);
    conference.on(MHConferenceEvents.CONNECTION_INTERRUPTED, onInterrupted);
}

/**
 * Removes the conference connection event listeners.
 *
 * @param {MHConference} conference - The conference instance.
 * @private
 * @returns {void}
 */
function _unbindConferenceConnectionListener(conference) {
    if (_connectionHandlers) {
        const c = conference || _connectionHandlers.conference;

        c.off(MHConferenceEvents.CONNECTION_ESTABLISHED, _connectionHandlers.onEstablished);
        c.off(MHConferenceEvents.CONNECTION_RESTORED, _connectionHandlers.onRestored);
        c.off(MHConferenceEvents.CONNECTION_INTERRUPTED, _connectionHandlers.onInterrupted);
        _connectionHandlers = null;
    }
}

/**
 * Binds all the helper functions needed by torture.
 *
 * @param {Store} store - The redux store.
 * @private
 * @returns {void}
 */
function _bindTortureHelpers(store) {
    const { getState } = store;

    // We bind helpers only if testing mode is enabled
    if (!isTestModeEnabled(getState())) {
        return;
    }

    // All torture helper methods go in here
    getMeetHourGlobalNS().testing = {
        getRemoteVideoType: getRemoteVideoType.bind(null, store),
        isLargeVideoReceived: isLargeVideoReceived.bind(null, store),
        isRemoteVideoReceived: isRemoteVideoReceived.bind(null, store)
    };
}

/**
 * The handler function for conference connection events which wil store the
 * latest even name in the Redux store of feature testing.
 *
 * @param {string} event - One of the lib-meet-hour MHConferenceEvents.
 * @param {Function} dispatch - The dispatch function of the current Redux
 * store.
 * @returns {void}
 * @private
 */
function _onConnectionEvent(event, dispatch) {
    switch (event) {
    case MHConferenceEvents.CONNECTION_ESTABLISHED:
    case MHConferenceEvents.CONNECTION_INTERRUPTED:
    case MHConferenceEvents.CONNECTION_RESTORED:
        dispatch(setConnectionState(event));
        break;
    default:
        logger.error(`onConnectionEvent - unsupported event type: ${event}`);
        break;
    }
}

